import React from 'react';

/* Shared print-portfolio primitives: hard-shadow cards, pastel tags,
   tracked labels, mono flow blocks, big stats. */

const tintClass = {
  paper: 'bg-paper',
  mint: 'bg-tint-mint',
  blue: 'bg-tint-blue',
  cream: 'bg-tint-cream',
};

export const Card = ({ tint = 'paper', className = '', children }) => (
  <div
    className={`border-[1.5px] border-ink rounded-xl shadow-hard ${tintClass[tint] || tintClass.paper} ${className}`}
  >
    {children}
  </div>
);

export const Label = ({ as: As = 'p', className = '', children }) => (
  <As className={`eyebrow ${className}`}>{children}</As>
);

const tagClass = {
  yellow: 'bg-tag-yellow border-tag-yellow-line',
  mint: 'bg-tag-mint border-tag-mint-line',
  blue: 'bg-tag-blue border-tag-blue-line',
  pink: 'bg-tag-pink border-tag-pink-line',
};

const tagOrder = ['yellow', 'mint', 'blue', 'pink'];

export const Tag = ({ tone, index = 0, children }) => {
  const key = tone || tagOrder[index % tagOrder.length];
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-full border text-[11px] sm:text-xs font-bold text-ink ${tagClass[key]}`}
    >
      {children}
    </span>
  );
};

/* Inline formatting for copy held in the data file:
   {{...}} highlighter mark, **...** bold, *...* italic. */
export const Marked = ({ text }) => {
  if (!text) return null;
  const parts = String(text).split(
    /(\{\{[^}]*\}\}|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*)/g
  );
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('{{') && part.endsWith('}}')) {
          return (
            <span key={i} className="mark mark-underline font-medium text-ink">
              {part.slice(2, -2)}
            </span>
          );
        }
        const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (link) {
          return (
            <a
              key={i}
              href={link[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-ink underline decoration-flag decoration-2 underline-offset-2 hover:text-flag transition-colors"
            >
              {link[1]}
            </a>
          );
        }
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={i} className="font-semibold text-ink">
              {part.slice(2, -2)}
            </strong>
          );
        }
        if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
          return <em key={i}>{part.slice(1, -1)}</em>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

export const Prose = ({ text, className = '' }) => (
  <p className={`text-sm sm:text-[15px] text-ink-soft leading-relaxed ${className}`}>
    <Marked text={text} />
  </p>
);
